<template>
  <div class="exchange-session stack tight">
    <UiPanel tone="econ">
      <div class="header">
        <div>
          <h3 class="title">{{ ICONS.places.exchange }} Exchange</h3>
          <div class="subtitle text-muted">Global order book, orders, and inbox.</div>
        </div>
        <div class="header-right">
          <UiChip v-if="loadingAny" variant="muted">Loading</UiChip>
          <UiChip v-else-if="isExchangeStale" variant="warning">Stale</UiChip>
        </div>
      </div>

      <div class="controls">
        <UiTabs v-model="tab" :tabs="tabs" tone="econ" />
        <div class="text-muted">Market data updates automatically.</div>
      </div>
    </UiPanel>

    <UiCallout v-if="!available" variant="warning">
      {{ blockedReason || "The Exchange terminal is only available at a bank." }}
    </UiCallout>

    <template v-else>
      <UiCallout v-if="error" variant="danger">{{ error }}</UiCallout>

      <div v-if="tab === 'browse'" class="browse-grid">
        <UiPanel tone="econ">
          <UiSectionHeader>
            <template #title>Search</template>
          </UiSectionHeader>

          <label class="search">
            <span class="sr-only">Search market items</span>
            <input v-model="query" class="ui-input" placeholder="Search market items…" />
          </label>

          <div v-if="itemsLoading" class="stack tight" style="margin-top: 0.75rem;">
            <UiSkeleton height="42px" />
            <UiSkeleton height="42px" />
            <UiSkeleton height="42px" />
          </div>

          <div v-else-if="items.length === 0" class="text-muted" style="margin-top: 0.75rem;">
            <template v-if="query.trim()">No results.</template>
            <template v-else>Start typing to search.</template>
          </div>

          <div v-else class="list" style="margin-top: 0.75rem;">
            <button
              v-for="item in items"
              :key="item.id"
              type="button"
              class="item-row"
              :class="{ selected: item.id === selectedItemId }"
              @click="selectItem(item)"
	            >
	              <span class="icon" aria-hidden="true">{{ itemIconForType(item.type) }}</span>
	              <span class="label">
	                <span class="name">{{ item.name }}</span>
	                <span class="text-muted stat-mono num">{{ formatNumber(item.basePrice) }} gp • {{ markForItemId(item.id) }}</span>
	              </span>
	            </button>
	          </div>
	        </UiPanel>

	        <UiPanel v-if="selectedItem" tone="econ">
	          <UiSectionHeader>
	            <template #title>Order Book</template>
	            <template #actions>
	              <UiChip variant="muted">{{ itemIconForType(selectedItem.type) }} {{ selectedItem.name }} {{ markForItemId(selectedItem.id) }}</UiChip>
	            </template>
	          </UiSectionHeader>

          <div v-if="bookLoading" class="stack tight">
            <UiSkeleton height="16px" />
            <UiSkeleton height="16px" />
            <UiSkeleton height="16px" />
          </div>
          <div v-else-if="orderBook" class="book">
            <div class="book-row">
              <div class="book-side">
                <div class="book-label">Buy median</div>
                <div class="book-value stat-mono num">{{ formatNumber(orderBook.buys.medianPrice) }} gp</div>
                <div class="text-muted stat-mono num">{{ formatNumber(orderBook.buys.totalQty) }} qty</div>
              </div>
              <div class="book-side">
                <div class="book-label">Sell median</div>
                <div class="book-value stat-mono num">{{ formatNumber(orderBook.sells.medianPrice) }} gp</div>
                <div class="text-muted stat-mono num">{{ formatNumber(orderBook.sells.totalQty) }} qty</div>
              </div>
            </div>
            <div class="text-muted" v-if="orderBook.referencePrice">
              Reference: <span class="stat-mono num">{{ formatNumber(orderBook.referencePrice.price) }} gp</span>
              ({{ orderBook.referencePrice.source }})
            </div>
          </div>
          <div v-else class="text-muted">Select an item.</div>
        </UiPanel>

        <UiPanel v-if="selectedItem" tone="econ">
          <UiSectionHeader>
            <template #title>Place Order</template>
          </UiSectionHeader>

          <div class="row wrap" style="justify-content: space-between;">
            <UiTabs v-model="orderType" :tabs="orderTypeTabs" tone="econ" />
            <UiChip variant="muted">Open {{ formatNumber(ordersOpenCount) }}</UiChip>
          </div>

          <div class="form">
            <div class="field">
              <div class="field-label">Quantity</div>
              <UiQuantity v-model="qty" :min="1" :max="maxOrderQty" :disabled="placing" />
              <div class="text-muted stat-mono num field-hint">Max {{ formatNumber(maxOrderQty) }}</div>
            </div>
            <label class="field">
              <span class="field-label">Limit (gp / unit)</span>
              <input v-model.number="limitPrice" class="ui-input" type="number" min="1" step="1" />
            </label>
          </div>

          <div class="text-muted stat-mono num" style="margin-top: 0.35rem;">
            Total {{ formatNumber(orderTotal) }} gp
          </div>

          <div class="row wrap" style="justify-content: flex-end;">
            <UiButton size="sm" :disabled="placing || !canPlace" @click="placeOrder">
              <template #icon>{{ orderType === 'buy' ? ICONS.states.active : ICONS.states.danger }}</template>
              Place
            </UiButton>
          </div>
        </UiPanel>
      </div>

      <div v-else-if="tab === 'orders'" class="stack tight">
        <UiPanel tone="econ">
          <UiSectionHeader>
            <template #title>Orders</template>
            <template #actions>
              <UiChip variant="muted">Open {{ formatNumber(ordersOpenCount) }}</UiChip>
            </template>
          </UiSectionHeader>
          <div v-if="ordersLoading" class="stack tight">
            <UiSkeleton height="42px" />
            <UiSkeleton height="42px" />
          </div>
          <div v-else-if="orders.length === 0" class="text-muted">No orders.</div>
	          <div v-else class="list">
	            <div v-for="o in orders" :key="o.id" class="order-row">
	              <div class="order-main">
	                <div class="order-title">
	                  <UiChip :variant="o.type === 'buy' ? 'active' : 'danger'">{{ o.type }}</UiChip>
	                  <span class="stat-mono num">{{ formatNumber(o.qtyRemaining) }}/{{ formatNumber(o.qtyTotal) }}</span>
	                  <span class="text-muted">{{ o.itemName || o.itemId }} <span class="item-mark stat-mono">{{ markForItemId(o.itemId) }}</span></span>
	                </div>
	                <div class="text-muted stat-mono num">{{ formatNumber(o.limitPrice) }} gp · {{ o.status }}</div>
	              </div>
	              <UiButton size="sm" variant="secondary" :disabled="ordersLoading" @click="cancelOrder(o.id)">Cancel</UiButton>
	            </div>
	          </div>
        </UiPanel>
      </div>

      <div v-else class="stack tight">
        <UiPanel tone="econ">
          <UiSectionHeader>
            <template #title>Inbox</template>
            <template #actions>
              <UiButton size="sm" variant="secondary" :disabled="inboxLoading || inbox.length === 0" @click="claimAll">
                Claim All
              </UiButton>
            </template>
          </UiSectionHeader>
          <div v-if="inboxLoading" class="stack tight">
            <UiSkeleton height="42px" />
            <UiSkeleton height="42px" />
          </div>
          <div v-else-if="inbox.length === 0" class="text-muted">Nothing waiting.</div>
          <div v-else class="list">
            <div v-for="e in inbox" :key="e.id" class="order-row">
	              <div class="order-main">
	                <div class="order-title">
	                  <span aria-hidden="true">{{ e.itemId ? itemIconForType(selectedItemById(e.itemId)?.type) : ICONS.itemsByType.currency }}</span>
	                  <span class="text-muted">
	                    {{ e.itemName || e.itemId || 'Gold' }}
	                    <span v-if="e.itemId" class="item-mark stat-mono">{{ markForItemId(e.itemId) }}</span>
	                  </span>
	                  <span v-if="e.qty" class="stat-mono num">×{{ formatNumber(e.qty) }}</span>
	                  <span v-if="e.gold" class="stat-mono num">+{{ formatNumber(e.gold) }} gp</span>
	                </div>
	                <div class="text-muted stat-mono num">{{ formatDateTime(e.createdAt) }}</div>
	              </div>
              <UiButton size="sm" variant="secondary" :disabled="inboxLoading" @click="claimOne(e.id)">Claim</UiButton>
            </div>
          </div>
        </UiPanel>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import type { MarketInboxEntry, MarketItemSummary, MarketOrderBook, MarketOrderSummary } from "@idle-rpg/shared";
import { apiClaimMarketInbox, apiMarketCancelOrder, apiMarketItems, apiMarketPlaceOrder } from "../../../api/client";
import { useSessionSocket } from "../../../composables/useSessionSocket";
import { ICONS } from "../../../lib/icons";
import { formatDateTime, formatNumber } from "../../../lib/format";
import { getItemMarkDisplay } from "../../../lib/itemMarks";
import { itemIconForType } from "../../../lib/itemIcons";
import { useItemMarkMode } from "../../../composables/useItemMarkMode";
import { useSessionStore } from "../../../store/session";
import { useToast } from "../../../composables/useToast";
import UiButton from "../../../components/ui/UiButton.vue";
import UiCallout from "../../../components/ui/UiCallout.vue";
import UiChip from "../../../components/ui/UiChip.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import UiQuantity from "../../../components/ui/UiQuantity.vue";
import UiSectionHeader from "../../../components/ui/UiSectionHeader.vue";
import UiSkeleton from "../../../components/ui/UiSkeleton.vue";
import UiTabs from "../../../components/ui/UiTabs.vue";

const props = defineProps<{
  available: boolean;
  blockedReason?: string | null;
}>();

const session = useSessionStore();
const { addToast } = useToast();
const { mode: itemMarkMode } = useItemMarkMode();
const sessionSocket = useSessionSocket();
const EXCHANGE_STALE_MS = 2 * 60 * 1000;
const lastUpdatedAt = ref<number | null>(null);
const isExchangeStale = computed(() => {
  if (!lastUpdatedAt.value) return false;
  return Date.now() - lastUpdatedAt.value > EXCHANGE_STALE_MS;
});
let wsUnsubscribers: Array<() => void> = [];

function startExchangeStreams() {
  stopExchangeStreams();
  if (!props.available) return;
  sessionSocket.subscribe(["market.orders", "market.inbox", "market.book"]);
  wsUnsubscribers.push(
    sessionSocket.onEvent("market.orders", (msg) => {
      if (msg.event !== "market.orders.updated") return;
      void loadOrders();
    })
  );
  wsUnsubscribers.push(
    sessionSocket.onEvent("market.inbox", (msg) => {
      if (msg.event !== "market.inbox.updated") return;
      void loadInbox();
    })
  );
  wsUnsubscribers.push(
    sessionSocket.onEvent("market.book", (msg) => {
      if (msg.event !== "market.book.updated") return;
      if (!selectedItemId.value) return;
      const data = msg.data;
      if (data && typeof data === "object" && "itemId" in data && (data as any).itemId !== selectedItemId.value) return;
      void loadBook();
    })
  );
}

function stopExchangeStreams() {
  while (wsUnsubscribers.length) {
    try {
      wsUnsubscribers.pop()?.();
    } catch {
      // ignore
    }
  }
  sessionSocket.unsubscribe(["market.orders", "market.inbox", "market.book"]);
}

function markForItemId(itemId: string): string {
  return getItemMarkDisplay(itemMarkMode.value, itemId);
}

const tab = ref<"browse" | "orders" | "inbox">("browse");
const tabs = computed(() => [
  { value: "browse", label: "Browse", icon: ICONS.actions.search },
  { value: "orders", label: "Orders", icon: ICONS.actions.orders },
  { value: "inbox", label: "Inbox", icon: ICONS.actions.inbox }
]);

const query = ref("");
const items = ref<MarketItemSummary[]>([]);
const itemsLoading = ref(false);
const selectedItemId = ref<string | null>(null);
const selectedItem = computed(() => items.value.find((i) => i.id === selectedItemId.value) ?? null);

const orderBook = ref<MarketOrderBook | null>(null);
const bookLoading = ref(false);

const orders = ref<MarketOrderSummary[]>([]);
const ordersOpenCount = ref(0);
const ordersLoading = ref(false);

const inbox = ref<MarketInboxEntry[]>([]);
const inboxLoading = ref(false);

const placing = ref(false);
const orderType = ref<"buy" | "sell">("buy");
const qty = ref<number>(1);
const limitPrice = ref<number>(1);

const orderTypeTabs = computed(() => [
  { value: "buy", label: "Buy", icon: ICONS.states.active },
  { value: "sell", label: "Sell", icon: ICONS.states.danger }
]);

const error = ref<string | null>(null);

const loadingAny = computed(() => itemsLoading.value || bookLoading.value || ordersLoading.value || inboxLoading.value || placing.value);

function requestId() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : Math.random().toString(36).slice(2);
}

async function loadItems() {
  if (!session.token || !session.character) return;
  const q = query.value.trim();
  if (!q) {
    items.value = [];
    return;
  }
  itemsLoading.value = true;
  error.value = null;
  const res = await apiMarketItems(session.token, session.character.id, q || undefined);
  itemsLoading.value = false;
  if (!res.ok || !res.data) {
    error.value = "error" in res ? res.error?.message || "Failed to load items" : "Failed to load items";
    return;
  }
  items.value = res.data.items;
}

async function loadBook() {
  if (!session.token || !session.character) return;
  if (!selectedItemId.value) return;
  bookLoading.value = true;
  error.value = null;
  try {
    const snapshot = await sessionSocket.requestSnapshot("market.book", { itemId: selectedItemId.value });
    orderBook.value = snapshot.data as MarketOrderBook;
    limitPrice.value = Math.max(1, Math.floor(Number(orderBook.value.basePrice || 1)));
    lastUpdatedAt.value = Date.now();
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    error.value = message || "Failed to load order book. Please retry.";
  } finally {
    bookLoading.value = false;
  }
}

async function loadOrders() {
  if (!session.token || !session.character) return;
  ordersLoading.value = true;
  error.value = null;
  try {
    const snapshot = await sessionSocket.requestSnapshot("market.orders");
    orders.value = snapshot.data.orders ?? [];
    ordersOpenCount.value = snapshot.data.openCount ?? 0;
    lastUpdatedAt.value = Date.now();
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    error.value = message || "Failed to load orders. Please retry.";
  } finally {
    ordersLoading.value = false;
  }
}

async function loadInbox() {
  if (!session.token || !session.character) return;
  inboxLoading.value = true;
  error.value = null;
  try {
    const snapshot = await sessionSocket.requestSnapshot("market.inbox");
    inbox.value = snapshot.data.entries ?? [];
    lastUpdatedAt.value = Date.now();
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    error.value = message || "Failed to load inbox. Please retry.";
  } finally {
    inboxLoading.value = false;
  }
}

async function refreshAll() {
  if (!props.available) return;
  await Promise.all([loadItems(), loadOrders(), loadInbox()]);
  await loadBook();
  lastUpdatedAt.value = Date.now();
}

function selectItem(item: MarketItemSummary) {
  selectedItemId.value = item.id;
  loadBook();
}

const inventoryQtyForSelected = computed(() => {
  if (!session.character || !selectedItemId.value) return 0;
  let total = 0;
  for (const entry of session.character.inventory ?? []) {
    if (entry.itemId !== selectedItemId.value) continue;
    total += Math.floor(Number(entry.quantity || 0));
  }
  return Math.max(0, total);
});

const maxOrderQty = computed(() => {
  if (!selectedItemId.value) return 1;
  const cap = 999_999;
  if (orderType.value === "sell") return Math.max(1, Math.min(cap, inventoryQtyForSelected.value));
  const gold = session.character?.gold ?? 0;
  const price = Math.max(1, Math.floor(Number(limitPrice.value || 1)));
  return Math.max(1, Math.min(cap, Math.floor(gold / price)));
});

const orderTotal = computed(() => Math.max(0, Math.floor(qty.value) * Math.max(0, Math.floor(limitPrice.value))));

watch(
  () => maxOrderQty.value,
  (max) => {
    qty.value = Math.max(1, Math.min(Math.floor(qty.value), Math.max(1, Math.floor(max))));
  }
);

const canPlace = computed(() => {
  if (!selectedItemId.value) return false;
  if (qty.value < 1) return false;
  if (limitPrice.value < 1) return false;
  if (orderType.value === "sell" && inventoryQtyForSelected.value <= 0) return false;
  return qty.value <= maxOrderQty.value;
});

async function placeOrder() {
  if (!session.token || !session.character) return;
  if (!props.available) return;
  if (!selectedItemId.value) return;
  placing.value = true;
  const res = await apiMarketPlaceOrder(session.token, session.character.id, {
    itemId: selectedItemId.value,
    quantity: Math.max(1, Math.floor(qty.value)),
    limitPrice: Math.max(1, Math.floor(limitPrice.value)),
    type: orderType.value,
    clientRequestId: requestId()
  });
  placing.value = false;
  if (!res.ok) {
    addToast("error" in res ? res.error?.message || "Order failed" : "Order failed", "warning");
    return;
  }
  addToast("Order placed", "success");
  await loadOrders();
  await loadInbox();
}

async function cancelOrder(orderId: string) {
  if (!session.token || !session.character) return;
  ordersLoading.value = true;
  const res = await apiMarketCancelOrder(session.token, session.character.id, orderId);
  ordersLoading.value = false;
  if (!res.ok) {
    addToast("error" in res ? res.error?.message || "Cancel failed" : "Cancel failed", "warning");
    return;
  }
  addToast("Order canceled", "success");
  await loadOrders();
  await loadInbox();
}

async function claimOne(id: string) {
  if (!session.token || !session.character) return;
  inboxLoading.value = true;
  const res = await apiClaimMarketInbox(session.token, session.character.id, id);
  inboxLoading.value = false;
  if (!res.ok) {
    addToast("error" in res ? res.error?.message || "Claim failed" : "Claim failed", "warning");
    return;
  }
  addToast(`Claimed ${res.data?.claimed ?? 0}`, "success");
  await loadInbox();
}

async function claimAll() {
  if (!session.token || !session.character) return;
  inboxLoading.value = true;
  const res = await apiClaimMarketInbox(session.token, session.character.id);
  inboxLoading.value = false;
  if (!res.ok) {
    addToast("error" in res ? res.error?.message || "Claim failed" : "Claim failed", "warning");
    return;
  }
  addToast(`Claimed ${res.data?.claimed ?? 0}`, "success");
  await loadInbox();
}

function selectedItemById(id: string) {
  return items.value.find((i) => i.id === id) ?? null;
}

let queryTimer: number | null = null;
watch(
  () => query.value,
  () => {
    if (!props.available) return;
    if (queryTimer) window.clearTimeout(queryTimer);
    queryTimer = window.setTimeout(() => loadItems(), 180);
  }
);

watch(
  () => tab.value,
  (next) => {
    if (!props.available) return;
    if (next === "orders") loadOrders();
    if (next === "inbox") loadInbox();
  }
);

onMounted(async () => {
  if (props.available) {
    await refreshAll();
    startExchangeStreams();
  }
});

watch(
  () => props.available,
  async (next) => {
    if (next) {
      await refreshAll();
      startExchangeStreams();
      return;
    }
    stopExchangeStreams();
  }
);

onUnmounted(() => {
  stopExchangeStreams();
});
</script>

<style scoped>
.exchange-session {
  min-width: 0;
}

.header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.title {
  margin: 0;
  font-family: var(--font-display);
  letter-spacing: 0.02em;
}

.subtitle {
  font-size: 0.9rem;
  margin-top: 0.15rem;
}

.controls {
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.browse-grid {
  display: grid;
  gap: 1rem;
}

.search {
  display: block;
}

.ui-input {
  width: 100%;
}

.list {
  display: grid;
  gap: 0.6rem;
}

.item-row {
  display: grid;
  grid-template-columns: 26px 1fr;
  gap: 0.65rem;
  align-items: center;
  padding: 0.65rem 0.75rem;
  border-radius: var(--radius-2);
  border: 1px solid var(--glass-border-0);
  background: var(--glass-0);
  text-align: left;
}

.item-row.selected {
  border-color: color-mix(in srgb, var(--accent) 40%, var(--glass-border-0));
  box-shadow: var(--shadow-accent);
}

.label {
  min-width: 0;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.6rem;
}

.name {
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.book {
  display: grid;
  gap: 0.75rem;
}

.book-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.book-side {
  padding: 0.65rem 0.75rem;
  border-radius: var(--radius-2);
  border: 1px solid var(--glass-border-0);
  background: var(--glass-0);
}

.book-label {
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--fg-1);
}

.book-value {
  font-size: 1.1rem;
  font-weight: 800;
  margin-top: 0.25rem;
}

.form {
  margin-top: 0.75rem;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.field {
  display: grid;
  gap: 0.3rem;
}

.field-label {
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--fg-1);
}

.order-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.65rem 0.75rem;
  border-radius: var(--radius-2);
  border: 1px solid var(--glass-border-0);
  background: var(--glass-0);
}

.order-main {
  min-width: 0;
  display: grid;
  gap: 0.25rem;
}

.order-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
</style>
